/**
 * Logbook Page
 * 
 * Page for managing logbook records.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, Upload, Download } from "lucide-react";
import { useLocation } from "wouter";
import { LogbookEntryTable } from "./components/LogbookEntryTable";
import { ImportADIFDialog } from "./components/ImportADIFDialog";
import {
  useLogbookEntries,
  useDeleteLogbookEntry,
} from "./hooks/useLogbookEntries";
import { useAuth } from "@/contexts/AuthContext";
import { logbookService } from "@/services/logbookService";
import { toast } from "sonner";
import type { LogbookEntry } from "@/services/logbookService";

export function LogbookPage() {
  // Set page title
  React.useEffect(() => {
    document.title = "Logbook - ORARI";
  }, []);

  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Records state
  const [showImportDialog, setShowImportDialog] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [searchCallsign, setSearchCallsign] = React.useState("");
  const [dateFrom, setDateFrom] = React.useState("");
  const [dateTo, setDateTo] = React.useState("");

  // Build filters
  const filters = React.useMemo(() => {
    const f: any = {};
    if (searchCallsign.trim()) {
      f.callsign = searchCallsign.trim().toUpperCase();
    }
    if (dateFrom) {
      f.dateFrom = dateFrom;
    }
    if (dateTo) {
      f.dateTo = dateTo;
    }
    return f;
  }, [searchCallsign, dateFrom, dateTo]);

  // Fetch logbook entries for current user
  const { entries, loading, error, total, fetchEntries } = useLogbookEntries({
    userId: user?.id,
    filters,
  });

  // Delete logbook entry hook
  const { deleteEntry, deleting } = useDeleteLogbookEntry();

  // Entry handlers
  const handleDelete = async (entry: LogbookEntry) => {
    if (!confirm(`Are you sure you want to delete the logbook entry for ${entry.callsign}?`)) {
      return;
    }
    await deleteEntry(entry._id);
    // Refresh entries after deletion
    await fetchEntries();
  };

  const handleSearch = () => {
    fetchEntries();
  };

  const handleClearFilters = () => {
    setSearchCallsign("");
    setDateFrom("");
    setDateTo("");
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const filters: any = {};
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;

      const blob = await logbookService.exportADIF(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `logbook_${new Date().toISOString().split('T')[0]}.adi`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Logbook exported successfully");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to export logbook";
      toast.error("Export failed", {
        description: errorMessage,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex-1 space-y-4 p-6">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Logbook</h2>
        {user?.id && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowImportDialog(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import ADIF
            </Button>
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export ADIF
                </>
              )}
            </Button>
            <Button onClick={() => setLocation("/logbook/new")}>
              <Plus className="mr-2 h-4 w-4" />
              Create Logbook Entry
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter logbook records by callsign or date range</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="searchCallsign">Target Callsign</Label>
              <Input
                id="searchCallsign"
                placeholder="YB0DX"
                value={searchCallsign}
                onChange={(e) => setSearchCallsign(e.target.value.toUpperCase())}
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateFrom">Date From</Label>
              <Input
                id="dateFrom"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="dateTo">Date To</Label>
              <Input
                id="dateTo"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Searching...
                </>
              ) : (
                "Search"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleClearFilters}
              disabled={loading}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logbook Entries Table */}
      <LogbookEntryTable
        entries={entries}
        loading={loading}
        error={error}
        onDelete={handleDelete}
      />

      {/* Import ADIF Dialog */}
      {user?.id && (
        <ImportADIFDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          onImportComplete={() => {
            fetchEntries();
          }}
        />
      )}
    </div>
  );
}
